import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Inbox, CheckSquare, History, Target } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Panel, Spinner, Tag } from "@/components/common";
import { KpiSection } from "@/components/dashboard/KpiSection";
import { ScheduleCompare } from "@/components/dashboard/ScheduleCompare";
import { KeywordRevenue } from "@/components/dashboard/KeywordRevenue";
import { BudgetCap } from "@/components/BudgetCap";
import { GoLiveChecklist } from "@/components/GoLiveChecklist";
import { EmergencyPanel } from "@/components/EmergencyPanel";
import { ModeSwitch } from "@/components/ModeSwitch";
import { PacingPanel } from "@/components/PacingPanel";
import { QuotaMeter } from "@/components/QuotaMeter";
import { TrackingCheck } from "@/components/TrackingCheck";

const LeadsPanel = ({ l, ev }) => (
  <Panel title="Leads · 30 days" icon={Inbox} testid="dashboard-leads" right={<Link to="/leads" className="text-xs text-emerald-400">Open inbox</Link>}>
    <div className="mb-3 font-mono text-3xl font-bold text-slate-50" data-testid="dashboard-leads-total">{l.total_30d}</div>
    <div className="mb-3 flex flex-wrap gap-2">{Object.entries(l.by_status).map(([k, v]) => <Tag key={k} tone={v ? "cyan" : "muted"}>{k}: {v}</Tag>)}</div>
    <div className="space-y-1 text-xs text-slate-400">{Object.entries(l.by_page).map(([k, v]) => <div key={k} className="flex justify-between"><span>{k}</span><span className="font-mono">{v}</span></div>)}</div>
    <div className="mt-3 grid grid-cols-3 gap-2 border-t border-[#1e293b] pt-3 text-xs text-slate-400">
      <div>From ads <b className="block font-mono text-slate-100">{l.from_ads}</b></div>
      <div>WhatsApp clicks <b className="block font-mono text-slate-100" data-testid="dashboard-whatsapp-clicks">{ev.whatsapp_click}</b></div>
      <div>Call clicks <b className="block font-mono text-slate-100" data-testid="dashboard-call-clicks">{ev.call_click}</b></div>
    </div>
  </Panel>
);

const SyncPanel = ({ d, onSync, busy }) => (
  <Panel title="Data sync" icon={RefreshCw} testid="dashboard-sync" right={<button data-testid="dashboard-sync-now" onClick={onSync} disabled={busy || !d.account} className="cc-btn cc-btn-ghost px-3 py-1 text-xs">{busy ? <Spinner className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync now</button>}>
    <div className="space-y-1 text-sm text-slate-400">
      <div>Last pull: <span className="font-mono text-slate-200" data-testid="dashboard-last-sync">{fmtDate(d.snapshot?.pulled_at)}</span></div>
      <div>Auto-sync: every 24 h{d.auto_sync?.at && <> · last run {fmtDate(d.auto_sync.at)} {d.auto_sync.ok ? <Tag tone="emerald">ok</Tag> : <Tag tone="red">failed</Tag>}</>}</div>
      {d.auto_sync?.error && <div className="text-xs text-red-300">{d.auto_sync.error}</div>}
    </div>
  </Panel>
);

const ActivityPanel = ({ d }) => (
  <Panel title="Approvals & changes" icon={CheckSquare} testid="dashboard-activity" right={<Link to="/approvals" className="text-xs text-emerald-400">Open approvals</Link>}>
    <div className="mb-3 flex flex-wrap gap-2"><Tag tone={d.pending_proposals ? "amber" : "muted"} testid="dashboard-pending">{d.pending_proposals} waiting for you</Tag><Tag tone={d.execution_enabled ? "amber" : "muted"}>Live execution {d.execution_enabled ? "ON" : "OFF"}</Tag>
      <Tag tone={d.tracking.configured ? "emerald" : "muted"}><Target className="h-3 w-3" /> Conversion tracking {d.tracking.configured ? d.tracking.aw_id : "not set up"}</Tag></div>
    <ul className="space-y-1.5 text-xs">{d.recent_changes.map((c, i) => <li key={i} className="flex gap-2 text-slate-400"><History className="mt-0.5 h-3 w-3 shrink-0" /><span className="font-mono text-slate-500">{fmtDate(c.at)}</span><span className="text-slate-300">{c.action}</span><span className="truncate">{c.title}</span></li>)}</ul>
  </Panel>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/dashboard").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const sync = async () => {
    setBusy(true);
    try { await api.post("/google-ads/sync"); toast.success("Google Ads data synced"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return <Spinner />;
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Command center" title="Dashboard" description={d.account ? `${d.account.name} · the 3 TRL campaigns since launch by default` : "Connect Google Ads to see performance."}
        actions={<Link to="/launch-report" data-testid="dashboard-launch-report-link" className="cc-btn cc-btn-ghost px-3 py-1.5 text-xs">Launch report</Link>} />
      <KpiSection />
      <div className="mt-6 space-y-6"><ScheduleCompare /><KeywordRevenue /></div>
      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2"><PacingPanel /><TrackingCheck testid="dashboard-tracking-check" /><LeadsPanel l={d.leads} ev={d.events_30d} /><ActivityPanel d={d} /><SyncPanel d={d} onSync={sync} busy={busy} /></div>
        <div className="space-y-6"><EmergencyPanel onDone={load} /><ModeSwitch onChange={load} /><QuotaMeter testid="dashboard-quota-meter" /><BudgetCap b={d.budget} /><GoLiveChecklist /></div>
      </div>
    </div>
  );
}
